import React, { useState, useEffect } from 'react';
import {
  exportCalendarEvents,
  exportCalendarECA,
  importCalendarEvents,
  importCalendarECA,
  downloadJSON,
  readJSONFile
} from '../../lib/calendarDataUtils';
import './CalendarManagement.css';

// Data Form View Component - Read-only display
const DataFormView = ({ data }) => {
  if (!data) return null;

  const renderEventCard = (event, index) => (
    <div key={index} className="event-card">
      <div className="event-header">
        <h5 className="event-title">{event.title || event.title_en || 'Untitled Event'}</h5>
        <span className="event-type">{event.event_type || 'N/A'}</span>
      </div>
      
      <div className="event-details">
        <div className="detail-row">
          <span className="detail-label">日期:</span>
          <span className="detail-value">{event.event_date || 'N/A'}</span>
        </div>
        {event.start_time && (
          <div className="detail-row">
            <span className="detail-label">開始時間:</span>
            <span className="detail-value">{event.start_time}</span>
          </div>
        )}
        {event.end_time && (
          <div className="detail-row">
            <span className="detail-label">結束時間:</span>
            <span className="detail-value">{event.end_time}</span>
          </div>
        )}
        {event.duration_days > 1 && (
          <div className="detail-row">
            <span className="detail-label">持續天數:</span>
            <span className="detail-value">{event.duration_days} 天</span>
          </div>
        )}
        {event.description && (
          <div className="detail-row">
            <span className="detail-label">描述:</span>
            <span className="detail-value">{event.description}</span>
          </div>
        )}
        {event.location && (
          <div className="detail-row">
            <span className="detail-label">地點:</span>
            <span className="detail-value">{event.location}</span>
          </div>
        )}
        {event.cycle_day && (
          <div className="detail-row">
            <span className="detail-label">週期日:</span>
            <span className="detail-value">Day {event.cycle_day}</span>
          </div>
        )}
        <div className="detail-row">
          <span className="detail-label">優先級:</span>
          <span className={`priority-badge priority-${event.priority || 'medium'}`}>
            {event.priority || 'medium'}
          </span>
        </div>
        <div className="detail-row">
          <span className="detail-label">狀態:</span>
          <span className={`status-badge status-${event.status || 'pending'}`}>
            {event.status || 'pending'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderActivityCard = (activity, index) => (
    <div key={index} className="activity-card">
      <div className="activity-header">
        <h5 className="activity-title">{activity.name || activity.name_en || 'Untitled Activity'}</h5>
        <span className="activity-category">{activity.eca_categories?.name || 'N/A'}</span>
      </div>
      
      <div className="activity-details">
        {activity.description && (
          <div className="detail-row">
            <span className="detail-label">描述:</span>
            <span className="detail-value">{activity.description}</span>
          </div>
        )}
        {activity.max_participants && (
          <div className="detail-row">
            <span className="detail-label">最大參與人數:</span>
            <span className="detail-value">{activity.max_participants}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderCategoryCard = (category, index) => (
    <div key={index} className="category-card">
      <div className="category-header">
        <h5 className="category-title">{category.name || category.name_en || 'Untitled Category'}</h5>
        <div 
          className="category-color" 
          style={{ backgroundColor: category.color_code || '#3498db' }}
        ></div>
      </div>
      
      {category.description && (
        <div className="detail-row">
          <span className="detail-label">描述:</span>
          <span className="detail-value">{category.description}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="form-display">
      {/* Academic Year Info */}
      {data.academicYear && (
        <div className="academic-year-card">
          <h5>學年信息 (Academic Year)</h5>
          <div className="detail-row">
            <span className="detail-label">學年:</span>
            <span className="detail-value">{data.academicYear.year_code}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">開始日期:</span>
            <span className="detail-value">{data.academicYear.start_date}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">結束日期:</span>
            <span className="detail-value">{data.academicYear.end_date}</span>
          </div>
        </div>
      )}

      {/* School Events */}
      {data.schoolEvents && data.schoolEvents.length > 0 && (
        <div className="data-section-group">
          <h5>學校活動 (School Events) - {data.schoolEvents.length} 項</h5>
          <div className="cards-grid">
            {data.schoolEvents.map((event, index) => renderEventCard(event, index))}
          </div>
        </div>
      )}

      {/* ECA Activities */}
      {data.ecaActivities && data.ecaActivities.length > 0 && (
        <div className="data-section-group">
          <h5>課外活動 (ECA Activities) - {data.ecaActivities.length} 項</h5>
          <div className="cards-grid">
            {data.ecaActivities.map((activity, index) => renderActivityCard(activity, index))}
          </div>
        </div>
      )}

      {/* ECA Categories */}
      {data.ecaCategories && data.ecaCategories.length > 0 && (
        <div className="data-section-group">
          <h5>活動類別 (ECA Categories) - {data.ecaCategories.length} 項</h5>
          <div className="cards-grid">
            {data.ecaCategories.map((category, index) => renderCategoryCard(category, index))}
          </div>
        </div>
      )}

      {/* Cycle Configuration */}
      {data.cycleConfig && (
        <div className="cycle-config-card">
          <h5>週期配置 (Cycle Configuration)</h5>
          <div className="detail-row">
            <span className="detail-label">週期天數:</span>
            <span className="detail-value">{data.cycleConfig.cycle_days || 'N/A'}</span>
          </div>
          {data.cycleConfig.cycle_day_names && (
            <div className="detail-row">
              <span className="detail-label">週期日名稱:</span>
              <span className="detail-value">{JSON.stringify(data.cycleConfig.cycle_day_names)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Data Form Edit Component - Editable form
const DataFormEdit = ({ data, onDataChange, onEditComplete }) => {
  const [formData, setFormData] = useState(data);

  const handleEventChange = (index, field, value) => {
    const newData = { ...formData };
    if (newData.schoolEvents) {
      newData.schoolEvents[index] = { ...newData.schoolEvents[index], [field]: value };
      setFormData(newData);
      onDataChange(newData);
    }
  };

  const handleActivityChange = (index, field, value) => {
    const newData = { ...formData };
    if (newData.ecaActivities) {
      newData.ecaActivities[index] = { ...newData.ecaActivities[index], [field]: value };
      setFormData(newData);
      onDataChange(newData);
    }
  };

  const handleSave = () => {
    onEditComplete();
  };

  const renderEditableEventCard = (event, index) => (
    <div key={index} className="event-card editable">
      <div className="event-header">
        <input
          type="text"
          value={event.title || ''}
          onChange={(e) => handleEventChange(index, 'title', e.target.value)}
          className="form-input event-title-input"
          placeholder="事件標題"
        />
        <select
          value={event.event_type || ''}
          onChange={(e) => handleEventChange(index, 'event_type', e.target.value)}
          className="form-select event-type-select"
        >
          <option value="">選擇類型</option>
          <option value="ceremony">典禮</option>
          <option value="exam">考試</option>
          <option value="holiday">假期</option>
          <option value="activity">活動</option>
          <option value="meeting">會議</option>
        </select>
      </div>
      
      <div className="event-details">
        <div className="form-row">
          <label>日期:</label>
          <input
            type="date"
            value={event.event_date || ''}
            onChange={(e) => handleEventChange(index, 'event_date', e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-row">
          <label>開始時間:</label>
          <input
            type="time"
            value={event.start_time || ''}
            onChange={(e) => handleEventChange(index, 'start_time', e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-row">
          <label>結束時間:</label>
          <input
            type="time"
            value={event.end_time || ''}
            onChange={(e) => handleEventChange(index, 'end_time', e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-row">
          <label>描述:</label>
          <textarea
            value={event.description || ''}
            onChange={(e) => handleEventChange(index, 'description', e.target.value)}
            className="form-textarea"
            placeholder="事件描述"
          />
        </div>
        <div className="form-row">
          <label>地點:</label>
          <input
            type="text"
            value={event.location || ''}
            onChange={(e) => handleEventChange(index, 'location', e.target.value)}
            className="form-input"
            placeholder="事件地點"
          />
        </div>
        <div className="form-row">
          <label>優先級:</label>
          <select
            value={event.priority || 'medium'}
            onChange={(e) => handleEventChange(index, 'priority', e.target.value)}
            className="form-select"
          >
            <option value="low">低</option>
            <option value="medium">中</option>
            <option value="high">高</option>
          </select>
        </div>
        <div className="form-row">
          <label>狀態:</label>
          <select
            value={event.status || 'pending'}
            onChange={(e) => handleEventChange(index, 'status', e.target.value)}
            className="form-select"
          >
            <option value="pending">待定</option>
            <option value="confirmed">已確認</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="form-edit">
      <div className="edit-header">
        <h5>編輯模式 - 表單視圖</h5>
        <button onClick={handleSave} className="btn btn-save">
          💾 保存所有更改
        </button>
      </div>

      {/* School Events */}
      {formData.schoolEvents && formData.schoolEvents.length > 0 && (
        <div className="data-section-group">
          <h5>學校活動 (School Events) - {formData.schoolEvents.length} 項</h5>
          <div className="cards-grid">
            {formData.schoolEvents.map((event, index) => renderEditableEventCard(event, index))}
          </div>
        </div>
      )}

      {/* ECA Activities */}
      {formData.ecaActivities && formData.ecaActivities.length > 0 && (
        <div className="data-section-group">
          <h5>課外活動 (ECA Activities) - {formData.ecaActivities.length} 項</h5>
          <div className="cards-grid">
            {formData.ecaActivities.map((activity, index) => (
              <div key={index} className="activity-card editable">
                <div className="activity-header">
                  <input
                    type="text"
                    value={activity.name || ''}
                    onChange={(e) => handleActivityChange(index, 'name', e.target.value)}
                    className="form-input activity-title-input"
                    placeholder="活動名稱"
                  />
                </div>
                <div className="activity-details">
                  <div className="form-row">
                    <label>描述:</label>
                    <textarea
                      value={activity.description || ''}
                      onChange={(e) => handleActivityChange(index, 'description', e.target.value)}
                      className="form-textarea"
                      placeholder="活動描述"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CalendarManagement = () => {
  const [academicYear, setAcademicYear] = useState('2024-2025');
  const [exportType, setExportType] = useState('event');
  const [importFile, setImportFile] = useState(null);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [permanentSave, setPermanentSave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [previewData, setPreviewData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [viewMode, setViewMode] = useState('json'); // 'json' or 'form'

  // Available academic years (2020-2021 to 2025-2026 with migrated data)
  const availableYears = [
    '2020-2021',
    '2021-2022',
    '2022-2023',
    '2023-2024',
    '2024-2025',
    '2025-2026'
  ];

  // Export types
  const exportTypes = [
    { value: 'event', label: 'Calendar_Event (學校活動)', description: 'School events and cycle day configuration' },
    { value: 'eca', label: 'Calendar_ECA (課外活動)', description: 'ECA activities, events, and categories' }
  ];

  // Handle export
  const handleExport = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      let exportData;
      let filename;
      
      if (exportType === 'event') {
        exportData = await exportCalendarEvents(academicYear);
        filename = `Calendar_Event_${academicYear}_${new Date().toISOString().split('T')[0]}.json`;
      } else {
        exportData = await exportCalendarECA(academicYear);
        filename = `Calendar_ECA_${academicYear}_${new Date().toISOString().split('T')[0]}.json`;
      }
      
      downloadJSON(exportData, filename);
      
      setMessage(`Successfully exported ${exportType === 'event' ? 'school events' : 'ECA data'} for ${academicYear}`);
      setMessageType('success');
    } catch (error) {
      setMessage(`Export failed: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
      setMessage('');
    } else {
      setMessage('Please select a valid JSON file');
      setMessageType('error');
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!importFile) {
      setMessage('Please select a file to import');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      const importData = await readJSONFile(importFile);
      
      let result;
      if (importData.exportType === 'Calendar_Event') {
        result = await importCalendarEvents(importData, overwriteMode, permanentSave);
      } else if (importData.exportType === 'Calendar_ECA') {
        result = await importCalendarECA(importData, overwriteMode, permanentSave);
      } else {
        throw new Error('Invalid export type in file');
      }
      
      if (result.previewMode) {
        // Preview mode - show data for review
        setPreviewData(result.data);
        setImportResult(result);
        setMessage(result.message);
        setMessageType('info');
        setShowConfirmation(true);
      } else {
        // Permanent save completed
        setMessage(result.message);
        setMessageType('success');
        setImportFile(null);
        setPreviewData(null);
        setShowConfirmation(false);
        
        // Reset file input
        const fileInput = document.getElementById('import-file');
        if (fileInput) fileInput.value = '';
      }
      
    } catch (error) {
      setMessage(`Import failed: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Handle permanent save confirmation
  const handlePermanentSave = async () => {
    if (!importFile || !previewData) return;
    
    setLoading(true);
    setMessage('');
    
    try {
      const importData = await readJSONFile(importFile);
      
      let result;
      if (importData.exportType === 'Calendar_Event') {
        result = await importCalendarEvents(importData, overwriteMode, true);
      } else if (importData.exportType === 'Calendar_ECA') {
        result = await importCalendarECA(importData, overwriteMode, true);
      } else {
        throw new Error('Invalid export type in file');
      }
      
      setMessage(result.message);
      setMessageType('success');
      setImportFile(null);
      setPreviewData(null);
      setShowConfirmation(false);
      
      // Reset file input
      const fileInput = document.getElementById('import-file');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      setMessage(`Permanent save failed: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Cancel import
  const handleCancelImport = () => {
    setPreviewData(null);
    setShowConfirmation(false);
    setImportResult(null);
    setEditMode(false);
    setEditableData(null);
    setMessage('Import cancelled');
    setMessageType('info');
  };

  // Enter edit mode
  const handleEditData = () => {
    setEditableData(JSON.stringify(previewData, null, 2));
    setEditMode(true);
  };

  // Save edited data
  const handleSaveEdit = () => {
    try {
      const parsedData = JSON.parse(editableData);
      setPreviewData(parsedData);
      setEditMode(false);
      setMessage('Data edited successfully');
      setMessageType('success');
    } catch (error) {
      setMessage('Invalid JSON format. Please check your edits.');
      setMessageType('error');
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditableData(null);
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="calendar-management">
      <div className="management-header">
        <h2>📅 日曆數據管理</h2>
        <p>Import and export calendar data for different academic years</p>
      </div>

      <div className="management-grid">
        {/* Export Section */}
        <div className="management-section export-section">
          <h3>📤 導出日曆數據 (Export Calendar Data)</h3>
          
          <div className="form-group">
            <label htmlFor="export-year">選擇學年 (Academic Year):</label>
            <select
              id="export-year"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="form-select"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year}學年
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="export-type">選擇導出類型 (Export Type):</label>
            <select
              id="export-type"
              value={exportType}
              onChange={(e) => setExportType(e.target.value)}
              className="form-select"
            >
              {exportTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <small className="form-help">{exportTypes.find(t => t.value === exportType)?.description}</small>
          </div>

          <button
            onClick={handleExport}
            disabled={loading}
            className="btn btn-export"
          >
            {loading ? '導出中...' : '📥 導出數據'}
          </button>
        </div>

        {/* Import Section */}
        <div className="management-section import-section">
          <h3>📥 導入日曆數據 (Import Calendar Data)</h3>
          
          <div className="form-group">
            <label htmlFor="import-file">選擇JSON文件 (Select JSON File):</label>
            <input
              type="file"
              id="import-file"
              accept=".json"
              onChange={handleFileSelect}
              className="form-file"
            />
            <small className="form-help">
              支持 Calendar_Event 和 Calendar_ECA 格式的JSON文件
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={overwriteMode}
                onChange={(e) => setOverwriteMode(e.target.checked)}
                className="form-checkbox"
              />
              <span className="checkmark"></span>
              覆蓋現有數據 (Overwrite existing data)
            </label>
            <small className="form-help">
              勾選後將刪除目標學年的現有數據，請謹慎操作
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={permanentSave}
                onChange={(e) => setPermanentSave(e.target.checked)}
                className="form-checkbox"
              />
              <span className="checkmark"></span>
              永久保存到數據庫 (Permanently save to database)
            </label>
            <small className="form-help">
              預設為預覽模式，勾選後將永久保存數據。建議先預覽確認無誤後再保存。
            </small>
          </div>

          <button
            onClick={handleImport}
            disabled={!importFile || loading}
            className="btn btn-import"
          >
            {loading ? '導入中...' : '📤 導入數據'}
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`message ${messageType}`}>
          <span className="message-icon">
            {messageType === 'success' ? '✅' : messageType === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span className="message-text">{message}</span>
        </div>
      )}

             {/* Preview and Confirmation Dialog */}
       {showConfirmation && previewData && (
         <div className="preview-section">
           <div className="preview-header">
             <h3>📋 數據預覽 (Data Preview)</h3>
             <p>請檢查以下數據，確認無誤後選擇操作</p>
           </div>
           
           <div className="preview-content">
             <div className="preview-summary">
               <h4>導入摘要 (Import Summary)</h4>
               <div className="summary-grid">
                 {importResult?.importedEvents && (
                   <div className="summary-item">
                     <span className="summary-label">學校活動:</span>
                     <span className="summary-value">{importResult.importedEvents}</span>
                   </div>
                 )}
                 {importResult?.importedActivities && (
                   <div className="summary-item">
                     <span className="summary-label">課外活動:</span>
                     <span className="summary-value">{importResult.importedActivities}</span>
                   </div>
                 )}
                 {importResult?.importedCategories && (
                   <div className="summary-item">
                     <span className="summary-label">活動類別:</span>
                     <span className="summary-value">{importResult.importedCategories}</span>
                   </div>
                 )}
                 {importResult?.cycleConfig && (
                   <div className="summary-item">
                     <span className="summary-label">週期配置:</span>
                     <span className="summary-value">{importResult.cycleConfig === 'ready' ? '已準備' : '無'}</span>
                   </div>
                 )}
               </div>
             </div>

                           {/* Data Display and Editing */}
              <div className="data-section">
                <div className="data-header">
                  <h4>📄 數據內容 (Data Content)</h4>
                  <div className="view-toggle">
                    <button
                      className={`toggle-btn ${viewMode === 'json' ? 'active' : ''}`}
                      onClick={() => setViewMode('json')}
                    >
                      📄 JSON 視圖
                    </button>
                    <button
                      className={`toggle-btn ${viewMode === 'form' ? 'active' : ''}`}
                      onClick={() => setViewMode('form')}
                    >
                      📋 表單視圖
                    </button>
                  </div>
                  <div className="data-actions">
                    {!editMode ? (
                      <button
                        onClick={handleEditData}
                        className="btn btn-edit"
                      >
                        ✏️ 編輯數據
                      </button>
                    ) : (
                      <div className="edit-actions">
                        <button
                          onClick={handleSaveEdit}
                          className="btn btn-save"
                        >
                          💾 保存編輯
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="btn btn-cancel-edit"
                        >
                          ❌ 取消編輯
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {!editMode ? (
                  // View Mode
                  viewMode === 'json' ? (
                    <div className="json-display">
                      <pre className="json-content">{JSON.stringify(previewData, null, 2)}</pre>
                    </div>
                  ) : (
                    <div className="form-display">
                      <DataFormView data={previewData} />
                    </div>
                  )
                ) : (
                  // Edit Mode
                  viewMode === 'json' ? (
                    <div className="json-edit">
                      <textarea
                        value={editableData}
                        onChange={(e) => setEditableData(e.target.value)}
                        className="json-textarea"
                        placeholder="Edit JSON data here..."
                        rows={20}
                      />
                      <div className="edit-help">
                        <p><strong>編輯提示:</strong> 您可以修改JSON數據內容。請確保格式正確，否則無法保存。</p>
                        <p>支持修改: 事件標題、日期、時間、描述等字段。</p>
                      </div>
                    </div>
                  ) : (
                    <div className="form-edit">
                      <DataFormEdit 
                        data={previewData} 
                        onDataChange={setPreviewData}
                        onEditComplete={() => setEditMode(false)}
                      />
                    </div>
                  )
                )}
              </div>

             <div className="preview-actions">
               <div className="action-buttons">
                 <button
                   onClick={handlePermanentSave}
                   disabled={loading}
                   className="btn btn-confirm"
                 >
                   {loading ? '保存中...' : '✅ 確認永久保存'}
                 </button>
                 <button
                   onClick={handleCancelImport}
                   disabled={loading}
                   className="btn btn-cancel"
                 >
                   ❌ 取消導入
                 </button>
               </div>
               <div className="action-note">
                 <p><strong>注意:</strong> 點擊"確認永久保存"後，數據將被永久保存到數據庫。</p>
                 <p>如果選擇了"覆蓋現有數據"，現有數據將被永久刪除。</p>
               </div>
             </div>
           </div>
         </div>
       )}

      {/* Instructions */}
      <div className="instructions">
        <h3>📋 使用說明 (Instructions)</h3>
        
        <div className="instruction-grid">
                     <div className="instruction-item">
             <h4>🔄 工作流程 (Workflow)</h4>
             <ol>
               <li><strong>導出:</strong> 從當前系統導出日曆數據為JSON文件</li>
               <li><strong>預覽:</strong> 導入數據進行預覽檢查（預設模式）</li>
               <li><strong>編輯:</strong> 在預覽界面直接編輯JSON數據內容</li>
               <li><strong>確認:</strong> 檢查無誤後選擇永久保存</li>
               <li><strong>保存:</strong> 二次確認後永久保存到數據庫</li>
             </ol>
           </div>

                     <div className="instruction-item">
             <h4>📁 文件格式 (File Format)</h4>
             <ul>
               <li><strong>Calendar_Event:</strong> 包含學校活動、週期日配置</li>
               <li><strong>Calendar_ECA:</strong> 包含課外活動、活動類別、分類</li>
               <li><strong>元數據:</strong> 包含導出日期、版本、學年信息</li>
               <li><strong>雙視圖模式:</strong> JSON視圖和表單視圖，便於查看和編輯</li>
             </ul>
           </div>

                     <div className="instruction-item">
             <h4>⚠️ 注意事項 (Important Notes)</h4>
             <ul>
               <li>導入前請備份現有數據</li>
               <li>預設為預覽模式，不會永久保存數據</li>
               <li>可在預覽界面直接編輯JSON數據</li>
               <li>覆蓋模式將永久刪除現有數據</li>
               <li>永久保存需要二次確認</li>
               <li>確保JSON文件格式正確</li>
               <li>建議在測試環境中先試用</li>
             </ul>
           </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarManagement;
